import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Loads data for a screen.
 *
 * Small on purpose — the panel has one shape of data need (fetch on mount,
 * refetch on filter change, reload after a mutation) and does not warrant a
 * query library. It guards the two things hand-rolled effects usually get
 * wrong: a response arriving after the component unmounted, and a slow response
 * from a stale filter overwriting a fast one from the current filter.
 */

export interface AsyncState<T> {
  data: T | null
  error: unknown
  loading: boolean
  /** Refetch with the current inputs; also used after a mutation. */
  reload: () => void
}

export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  // Only the newest request may write state.
  const requestId = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)

    loadRef
      .current()
      .then((result) => {
        if (!mounted.current || id !== requestId.current) return
        setData(result)
        setLoading(false)
      })
      .catch((cause: unknown) => {
        if (!mounted.current || id !== requestId.current) return
        setError(cause)
        setLoading(false)
      })
    // `load` is intentionally excluded: callers pass an inline closure, and the
    // explicit dependency list is what decides when to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { data, error, loading, reload }
}
